import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useRecipeStudio } from "./RecipeStudioContext";
import BottomActionBar from "./BottomActionBar";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    ArrowLeft,
    ArrowRight,
    Check,
    Flame,
    HeartPulse,
    Activity,
    Leaf,
    Sprout,
    WheatOff,
    MilkOff,
    CircleSlash,
    ChevronDown,
    Globe,
    MessageSquare,
    Shell,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────
// TYPES & CONSTANTS
// ─────────────────────────────────────────────────────────────

interface DietaryOption {
    id: string;
    label: string;
    icon: React.ElementType;
    description: string;
    /** Resting icon-chip classes (per-option tint). */
    tint: string;
}

type TierType = "philosophy" | "lifestyle" | "restriction";

const MAX_SELECTIONS = 3;

// Tier 1: Anchors (Protected) - Max 1 per group, swapping only
const PHILOSOPHY_OPTIONS: DietaryOption[] = [
    { id: "LowCarb", label: "Low-Carb", icon: Flame, description: "Fewer carbs, more healthy fats", tint: "bg-amber-50 text-amber-600" },
    { id: "HealthyLight", label: "Healthy & Light", icon: HeartPulse, description: "Calorie-conscious, nutrient-dense", tint: "bg-rose-50 text-rose-600" },
    { id: "GlucoseFriendly", label: "Glucose-Friendly", icon: Activity, description: "Steady blood sugar focus", tint: "bg-teal-50 text-teal-600" },
];

const LIFESTYLE_OPTIONS: DietaryOption[] = [
    { id: "Vegetarian", label: "Vegetarian", icon: Leaf, description: "No meat or fish", tint: "bg-emerald-50 text-emerald-600" },
    { id: "Vegan", label: "Vegan", icon: Sprout, description: "Fully plant-based", tint: "bg-lime-50 text-lime-600" },
];

// Tier 2: Modifiers (Evictable) - FIFO eviction when limit hit
const RESTRICTION_OPTIONS: DietaryOption[] = [
    { id: "GlutenFree", label: "Gluten-Free", icon: WheatOff, description: "No wheat, barley or rye", tint: "bg-sky-50 text-sky-600" },
    { id: "DairyFree", label: "Dairy-Free", icon: MilkOff, description: "No milk, butter or cheese", tint: "bg-violet-50 text-violet-600" },
    { id: "NutFree", label: "Nut-Free", icon: CircleSlash, description: "No tree nuts or peanuts", tint: "bg-red-50 text-red-600" },
    { id: "ShellfishFree", label: "Shellfish-Free", icon: Shell, description: "No crustaceans or molluscs", tint: "bg-orange-50 text-orange-700" },
];

const LANGUAGE_OPTIONS = [
    "English (UK)", "French", "Spanish", "German", "Italian", "Portuguese",
    "Chinese (Simplified)", "Chinese (Traditional)", "Japanese", "Korean",
    "Arabic", "Hindi", "Turkish", "Greek", "Dutch", "Swedish", "Norwegian",
    "Danish", "Finnish", "Polish", "Czech", "Hebrew", "Thai", "Vietnamese",
    "Indonesian", "Other (type manually)",
];

// Helper to get tier type for an option ID
const getTier = (id: string): TierType => {
    if (PHILOSOPHY_OPTIONS.some(o => o.id === id)) return "philosophy";
    if (LIFESTYLE_OPTIONS.some(o => o.id === id)) return "lifestyle";
    return "restriction";
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────

const Step3DietarySpecial: React.FC = () => {
    const {
        selectedDietaryTags,
        setSelectedDietaryTags,
        specialRequest,
        setSpecialRequest,
        language,
        setLanguage,
        languageCustom,
        setLanguageCustom,
    } = useRecipeStudio();
    const navigate = useNavigate();

    // ─────────────────────────────────────────────────────────────
    // WEIGHTED PRIORITY SELECTION LOGIC
    // ─────────────────────────────────────────────────────────────

    const handleSelection = useCallback((optionId: string) => {
        const tier = getTier(optionId);
        const isSelected = selectedDietaryTags.includes(optionId);

        // If already selected, just deselect
        if (isSelected) {
            setSelectedDietaryTags(selectedDietaryTags.filter(t => t !== optionId));
            return;
        }

        let newTags = [...selectedDietaryTags];

        // TIER 1: Anchors - Swap within group (max 1 per anchor group)
        if (tier === "philosophy") {
            // Remove any existing philosophy selection
            newTags = newTags.filter(t => getTier(t) !== "philosophy");
            newTags.push(optionId);
        } else if (tier === "lifestyle") {
            // Remove any existing lifestyle selection
            newTags = newTags.filter(t => getTier(t) !== "lifestyle");
            newTags.push(optionId);
        } else {
            // TIER 2: Modifiers - Evict oldest modifier if at limit
            newTags.push(optionId);

            // If over limit, evict oldest restriction (FIFO)
            while (newTags.length > MAX_SELECTIONS) {
                const restrictionIndex = newTags.findIndex(t => getTier(t) === "restriction");
                if (restrictionIndex !== -1) {
                    newTags.splice(restrictionIndex, 1);
                } else {
                    // Shouldn't happen, but safety: remove first item
                    newTags.shift();
                }
            }
        }

        setSelectedDietaryTags(newTags);
    }, [selectedDietaryTags, setSelectedDietaryTags]);

    const handleNext = () => navigate("../step/4");
    const handleBack = () => navigate("../step/2");

    // ─────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────

    return (
        <div className="max-w-2xl mx-auto px-4 pb-24 md:pb-8">
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-6"
            >
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                    Dietary Preferences
                </h1>
                <p className="text-slate-600 mt-1">
                    Optional • Select up to {MAX_SELECTIONS}
                </p>
            </motion.div>

            {/* Selection Counter */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-center mb-6"
            >
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100">
                    {[...Array(MAX_SELECTIONS)].map((_, i) => (
                        <div
                            key={i}
                            className={cn(
                                "w-2 h-2 rounded-full transition-all duration-300",
                                i < selectedDietaryTags.length
                                    ? "bg-orange-500 scale-110"
                                    : "bg-slate-300"
                            )}
                        />
                    ))}
                    <span className="ml-2 text-xs font-medium text-slate-700">
                        {selectedDietaryTags.length}/{MAX_SELECTIONS}
                    </span>
                </div>
            </motion.div>

            {/* Dietary sections — full-width stacked so every row is balanced */}
            <div className="space-y-7">
                {/* Diet Philosophy */}
                <Section
                    title="Diet Philosophy"
                    subtitle="Pick one approach — or skip"
                    delay={0.1}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {PHILOSOPHY_OPTIONS.map((option, i) => (
                            <OptionTile
                                key={option.id}
                                option={option}
                                isSelected={selectedDietaryTags.includes(option.id)}
                                onSelect={handleSelection}
                                delay={0.1 + i * 0.05}
                            />
                        ))}
                    </div>
                </Section>

                {/* Lifestyle */}
                <Section
                    title="Lifestyle"
                    subtitle="Pick one — or skip"
                    delay={0.2}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {LIFESTYLE_OPTIONS.map((option, i) => (
                            <OptionTile
                                key={option.id}
                                option={option}
                                isSelected={selectedDietaryTags.includes(option.id)}
                                onSelect={handleSelection}
                                delay={0.2 + i * 0.05}
                            />
                        ))}
                    </div>
                </Section>

                {/* Allergies & Restrictions */}
                <Section
                    title="Allergies & Restrictions"
                    subtitle="Pick any that apply"
                    delay={0.25}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {RESTRICTION_OPTIONS.map((option, i) => (
                            <OptionTile
                                key={option.id}
                                option={option}
                                isSelected={selectedDietaryTags.includes(option.id)}
                                onSelect={handleSelection}
                                delay={0.25 + i * 0.05}
                            />
                        ))}
                    </div>
                </Section>
            </div>

            {/* Language & Special Request - Compact Row */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4"
            >
                {/* Language Selection */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-medium text-slate-700">
                            Recipe Language
                        </span>
                    </div>
                    <div className="relative">
                        <select
                            value={language}
                            onChange={(e) => {
                                setLanguage(e.target.value);
                                if (e.target.value !== "Other (type manually)") setLanguageCustom("");
                            }}
                            className={cn(
                                "w-full appearance-none px-4 py-2.5 pr-10 rounded-xl",
                                "bg-white border-2 border-slate-200",
                                "text-slate-900 text-sm font-medium",
                                "focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20",
                                "transition-all duration-200 min-h-[44px]"
                            )}
                        >
                            {LANGUAGE_OPTIONS.map(lang => (
                                <option key={lang} value={lang}>{lang}</option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                    <AnimatePresence>
                        {language === "Other (type manually)" && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                            >
                                <Input
                                    placeholder="Enter language name"
                                    value={languageCustom}
                                    onChange={(e) => setLanguageCustom(e.target.value)}
                                    className="border-2 focus:border-orange-500"
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Special Request */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-medium text-slate-700">
                            Special Request
                        </span>
                        <span className="text-xs text-slate-400">(optional)</span>
                    </div>
                    <Input
                        placeholder="e.g., 'make it spicy', 'reduce sugar'"
                        value={specialRequest}
                        onChange={(e) => setSpecialRequest(e.target.value)}
                        className="border-2 focus:border-orange-500 py-2.5 rounded-xl min-h-[44px]"
                    />
                </div>
            </motion.div>

            {/* Desktop Navigation */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="hidden md:flex justify-between gap-4 mt-8"
            >
                <Button
                    variant="outline"
                    onClick={handleBack}
                    className="rounded-full px-6 h-11"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                </Button>
                <Button
                    onClick={handleNext}
                    className={cn(
                        "rounded-full px-6 h-11",
                        "bg-gradient-to-r from-orange-500 to-pink-500",
                        "hover:from-orange-600 hover:to-pink-600",
                        "shadow-md shadow-orange-500/25 hover:shadow-lg hover:shadow-orange-500/30",
                        "transition-all duration-300"
                    )}
                >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
            </motion.div>

            {/* Mobile Bottom Action Bar */}
            <BottomActionBar
                onBack={handleBack}
                onNext={handleNext}
                hideOnDesktop
            />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────

interface SectionProps {
    title: string;
    subtitle: string;
    delay?: number;
    children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, subtitle, delay = 0, children }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay }}
    >
        <div className="mb-3 flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-semibold text-slate-900">
                {title}
            </h2>
            <p className="text-xs text-slate-400">{subtitle}</p>
        </div>
        {children}
    </motion.div>
);

interface OptionTileProps {
    option: DietaryOption;
    isSelected: boolean;
    onSelect: (id: string) => void;
    delay?: number;
}

const OptionTile: React.FC<OptionTileProps> = ({
    option,
    isSelected,
    onSelect,
    delay = 0,
}) => {
    const Icon = option.icon;

    return (
        <motion.button
            type="button"
            onClick={() => onSelect(option.id)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay }}
            whileTap={{ scale: 0.98 }}
            aria-pressed={isSelected}
            className={cn(
                "relative w-full text-left rounded-2xl p-4 transition-all duration-300",
                "flex items-center gap-3 bg-white min-h-[72px]",
                isSelected
                    ? "ring-2 ring-orange-500 shadow-lg shadow-orange-500/15"
                    : "ring-1 ring-slate-200/80 shadow-sm hover:shadow-md hover:ring-slate-300"
            )}
        >
            {/* Icon Container — per-option tint, brand gradient when selected */}
            <div
                className={cn(
                    "flex-shrink-0 flex items-center justify-center rounded-xl w-11 h-11 transition-all duration-300",
                    isSelected
                        ? "bg-gradient-to-br from-orange-500 to-pink-500 text-white shadow-md shadow-orange-500/25"
                        : option.tint
                )}
            >
                <Icon className="w-5 h-5" strokeWidth={2.2} />
            </div>

            {/* Text — label + description always visible */}
            <div className="flex-1 min-w-0">
                <h3
                    className={cn(
                        "font-semibold text-sm leading-tight transition-colors",
                        isSelected ? "text-orange-700" : "text-slate-900"
                    )}
                >
                    {option.label}
                </h3>
                <p
                    className={cn(
                        "text-xs mt-0.5 leading-snug transition-colors",
                        isSelected ? "text-orange-600/80" : "text-slate-500"
                    )}
                >
                    {option.description}
                </p>
            </div>

            {/* Selection Indicator */}
            <AnimatePresence>
                {isSelected && (
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center"
                    >
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.button>
    );
};

export default Step3DietarySpecial;
